/**
 * i18next configuration — bilingual EN/TH support.
 *
 * Default language is read from localStorage ('ydm-lang'),
 * falling back to navigator.language or 'en'.
 *
 * Usage in components:
 *   import { useTranslation } from 'react-i18next';
 *   const { t } = useTranslation();
 *   <h1>{t('home.title')}</h1>
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import th from './locales/th.json';

const LANG_KEY = 'ydm-lang';

function getInitialLanguage(): string {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'th') return stored;
  } catch {
    // localStorage blocked
  }
  // Check browser language
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('th')) {
    return 'th';
  }
  return 'en';
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    th: { translation: th },
  },
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React already escapes by default
  },
});

/**
 * Tell the DOCUMENT which language it is in, not just React.
 *
 * `index.html` ships `<html lang="th">` and nothing ever changed it, so an
 * English session served every word of its UI in English while the document
 * still claimed to be Thai. Nothing on screen shows it; a screen reader picks
 * the voice and the pronunciation rules from this attribute, and `lang` is also
 * what hyphenation and spell-check read.
 *
 * Done here rather than in a component because the language can change without
 * one rendering, and set once below for the initial load — `languageChanged`
 * does not fire for the language i18next starts with.
 */
function applyDocumentLanguage(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
}

applyDocumentLanguage(i18n.language);

// Persist language changes
i18n.on('languageChanged', (lng) => {
  applyDocumentLanguage(lng);
  try {
    localStorage.setItem(LANG_KEY, lng);
  } catch {
    // ignore
  }
});

export default i18n;
